import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import ProductCard from '../components/ProductCard';
import Cart from '../components/Cart';
import CategorySidebar from '../components/CategorySidebar';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Filter, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(data);
    } catch (error) {
      toast.error('Eroare la încărcarea produselor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleCategorySelect = (categoryId, subcategoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory(subcategoryId);
    setFilterOpen(false);
  };

  const addToCart = (product) => {
    const existing = cartItems.find((item) => item.id === product.id);
    if (existing) {
      setCartItems(
        cartItems.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setCartItems([...cartItems, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} adăugat în coș`);
  };

  const removeFromCart = (productId) => {
    setCartItems(cartItems.filter((item) => item.id !== productId));
    toast.info('Produs eliminat din coș');
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems(
      cartItems.map((item) => (item.id === productId ? { ...item, quantity: newQuantity } : item))
    );
  };

  const handleCheckout = async (clientNote) => {
    try {
      const orderItems = cartItems.map((item) => ({
        product_id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        is_quote_only: item.is_quote_only || false
      }));

      await axios.post(
        `${API}/orders`,
        { items: orderItems, client_note: clientNote || '' },
        { withCredentials: true }
      );

      toast.success('Comandă plasată cu succes!');
      setCartItems([]);
      navigate('/orders');
    } catch (error) {
      toast.error('Eroare la plasarea comenzii');
    }
  };

  const filteredProducts = useMemo(() => {
    const filtered = products.filter((product) => {
      if (selectedCategory && product.category !== selectedCategory) return false;
      if (selectedSubcategory && product.subcategory !== selectedSubcategory) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const nameMatch = product.name?.toLowerCase().includes(q);
        const descMatch = product.description?.toLowerCase().includes(q);
        if (!nameMatch && !descMatch) return false;
      }
      return true;
    });
    return filtered.sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', 'ro', { sensitivity: 'base' })
    );
  }, [products, selectedCategory, selectedSubcategory, searchQuery]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar cartCount={cartItems.length} />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar cartCount={cartItems.length} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Produse
          </h1>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Caută produse după nume..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
                data-testid="product-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="clear-search-button"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Mobile filter button */}
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="md:hidden shrink-0"
                  data-testid="mobile-filter-button"
                >
                  <Filter className="w-4 h-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-4">
                <SheetHeader className="mb-4">
                  <SheetTitle style={{ fontFamily: 'Outfit' }}>Filtrare Categorii</SheetTitle>
                </SheetHeader>
                <CategorySidebar
                  selectedCategory={selectedCategory}
                  selectedSubcategory={selectedSubcategory}
                  onSelect={handleCategorySelect}
                />
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {searchQuery && (
          <p
            className="mb-4 text-sm text-slate-600"
            style={{ fontFamily: 'IBM Plex Sans' }}
            data-testid="search-results-count"
          >
            {filteredProducts.length} {filteredProducts.length === 1 ? 'rezultat' : 'rezultate'} pentru „{searchQuery}"
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-[190px_1fr] gap-4">
          <div className="hidden md:block">
            <CategorySidebar
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              onSelect={handleCategorySelect}
            />
          </div>

          <div>
            {filteredProducts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
                <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                  {searchQuery
                    ? `Niciun produs găsit pentru „${searchQuery}"`
                    : 'Nu există produse în această categorie.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filteredProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onAddToCart={addToCart}
                    isAdmin={false}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Cart
        items={cartItems}
        onRemove={removeFromCart}
        onUpdateQuantity={updateQuantity}
        onCheckout={handleCheckout}
      />
    </div>
  );
};

export default Products;
